import React, { useState, useEffect } from 'react';
import { API } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Users, Calendar, DollarSign, Star } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const CoachDashboard = () => {
  const [bookings, setBookings] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [bookingsRes, clientsRes] = await Promise.all([
        API.get('/bookings/coach'),
        API.get('/bookings/coach/clients')
      ]);
      setBookings(bookingsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const upcomingBookings = bookings.filter(b => b.status === 'confirmed' || b.status === 'pending');
  const totalRevenue = bookings.filter(b => b.status === 'completed').reduce((sum, b) => sum + b.price, 0);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-bold mb-2">Coach Dashboard</h1>
        <p className="text-muted-foreground">Manage your clients and sessions</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader>
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mb-2">
              <Users className="w-6 h-6 text-white" />
            </div>
            <CardTitle className="text-3xl font-bold">{clients.length}</CardTitle>
            <CardDescription>Total Clients</CardDescription>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader>
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center mb-2">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <CardTitle className="text-3xl font-bold">{upcomingBookings.length}</CardTitle>
            <CardDescription>Upcoming Sessions</CardDescription>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader>
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-2">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
            <CardTitle className="text-3xl font-bold">${totalRevenue}</CardTitle>
            <CardDescription>Total Revenue</CardDescription>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader>
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center mb-2">
              <Star className="w-6 h-6 text-white" />
            </div>
            <CardTitle className="text-3xl font-bold">{bookings.filter(b => b.status === 'completed').length}</CardTitle>
            <CardDescription>Completed Sessions</CardDescription>
          </CardHeader>
        </Card>
      </div>

      {/* Upcoming Bookings */}
      <Card>
        <CardHeader>
          <CardTitle>Upcoming Sessions</CardTitle>
        </CardHeader>
        <CardContent>
          {upcomingBookings.length > 0 ? (
            <div className="space-y-4">
              {upcomingBookings.map(booking => (
                <Card key={booking.booking_id} className="bg-gray-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold">{booking.user_name || 'Client'}</p>
                        <p className="text-sm text-muted-foreground">
                          {booking.date} at {booking.time}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {booking.session_type.replace('_', ' ')}
                        </p>
                      </div>
                      <Badge className={
                        booking.status === 'confirmed' ? 'bg-green-600' : 'bg-yellow-600'
                      }>
                        {booking.status}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-4">No upcoming sessions</p>
          )}
        </CardContent>
      </Card>

      {/* Clients */}
      <Card>
        <CardHeader>
          <CardTitle>Your Clients</CardTitle>
        </CardHeader>
        <CardContent>
          {clients.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {clients.map(client => (
                <Card key={client.user_id} className="bg-gray-50">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold">{client.name}</p>
                        <p className="text-sm text-muted-foreground">{client.email}</p>
                        {client.children && client.children.length > 0 && (
                          <p className="text-xs text-muted-foreground mt-2">
                            Children: {client.children.map(c => c.name).join(', ')}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">{client.total_sessions} sessions</p>
                        <p className="text-xs text-muted-foreground">{client.completed_sessions} completed</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-4">No clients yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CoachDashboard;
